import React, { useState, useEffect } from "react";
import { Input, Button, Form, message, Spin } from "antd";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { useUpdateUser } from "../../hooks/useUsers";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";

const ProfileSection: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  // Get current user from auth hook (just to get the ID)
  const { data: authUser } = useAuth();

  // Fetch detailed user data using the ID
  const {
    data: userData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user", authUser?.id],
    queryFn: async () => {
      if (!authUser?.id) return null;
      const response = await apiClient.get(`/user/${authUser.id}`);
      return response.data.data;
    },
    enabled: !!authUser?.id,
  });

  // Update user mutation
  const updateUserMutation = useUpdateUser();

  useEffect(() => {
    // Populate form fields with user data when available
    if (userData) {
      // Split name into first and last name (if possible)
      const nameParts = userData.name ? userData.name.split(" ") : ["", ""];
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      form.setFieldsValue({
        firstName,
        lastName,
        email: userData.email || "",
        phone: userData.phone || "",
      });
    }
  }, [userData, form]);

  const handleEditToggle = () => {
    if (isEditing) {
      // If we're currently editing, try to save
      form.submit();
    } else {
      // If we're not editing, switch to edit mode
      setIsEditing(true);
    }
  };

  const onFinish = async (values: any) => {
    try {
      // Combine first and last name
      const name = `${values.firstName} ${values.lastName}`.trim();

      // Prepare user data for update
      const userUpdateData = {
        name: name,
        email: values.email,
        phone: values.phone,
        // Include other required fields from the current user
        user_type_id: userData?.user_type_id || 0,
        role_id: userData?.roles?.id,
        location_id: userData?.location_id || 0,
        status: true, // Assuming user is active
        gender: userData?.gender || "male",
        country: userData?.country || "",
        state: userData?.state || "",
        lga: userData?.lga || "",
      };

      if (!authUser?.id) {
        message.error("User ID not found");
        return;
      }

      // Call update mutation
      await updateUserMutation.mutateAsync({
        userId: authUser.id,
        userData: userUpdateData,
      });

      // Refetch user data after update
      refetch();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200 flex justify-center">
        <Spin size="large" tip="Loading user data..." />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Basic Info</h2>
        <Button
          icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
          onClick={handleEditToggle}
          loading={updateUserMutation.isPending}
          className="border border-gray-300 hover:border-gray-400 px-4 py-1.5 rounded-lg text-gray-700"
        >
          {isEditing ? "Save" : "Edit"}
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="grid grid-cols-2 gap-4"
      >
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[{ required: true, message: "Please enter your first name" }]}
        >
          <Input disabled={!isEditing} />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[{ required: true, message: "Please enter your last name" }]}
        >
          <Input disabled={!isEditing} />
        </Form.Item>

        <Form.Item name="email" label="Email Address">
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Phone Number"
          rules={[
            { required: true, message: "Please enter your phone number" },
          ]}
        >
          <Input disabled={!isEditing} />
        </Form.Item>

        <Form.Item label="Staff ID">
          <Input
            disabled
            value={
              userData?.id
                ? `A56${userData.id.toString().padStart(5, "0")}`
                : ""
            }
          />
        </Form.Item>

        <Form.Item label="Role">
          <Input disabled value={userData?.roles?.name || "Unknown"} />
        </Form.Item>
      </Form>
    </div>
  );
};

export default ProfileSection;
